import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import api from '../services/api'

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      
      // Initialize: restore token to API client
      initialize: () => {
        const state = get()
        if (state.token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${state.token}`
        }
      },
      
      login: async (gebruikersnaam, wachtwoord, totp_code) => {
        try {
          const payload = { gebruikersnaam, wachtwoord }
          if (totp_code) payload.totp_code = totp_code

          const response = await api.post('/auth/login', payload)

          // Privacy Policy flow: backend stuurt 200 met beperkt token
          if (response.data?.code === 'PRIVACY_POLICY_REQUIRED') {
            return {
              success: false,
              code: 'PRIVACY_POLICY_REQUIRED',
              ppToken: response.data.access_token,
              versie: response.data.versie,
              error: response.data.bericht,
            }
          }

          const { access_token, user_id, tenant_id, gebruikersnaam: username, is_admin, is_superuser, role, is_setup_completed, accepted_tos_at } = response.data

          if (!access_token) {
            throw new Error('No access token received')
          }

          set({
            token: access_token,
            // accepted_tos_at uit de backend: zonder dit veld vroeg het voorwaardenvenster na elke
            // login opnieuw om aanvaarding, ook als die al bewaard was.
            user: { id: user_id, tenant_id, gebruikersnaam: username, is_admin: is_admin || false, is_superuser: Boolean(is_superuser), role: role || null, is_setup_completed: is_setup_completed || false, accepted_tos_at: accepted_tos_at || null },
            isAuthenticated: true,
          })

          // Set token in API client
          api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

          return { success: true }
        } catch (error) {
          console.error('[AUTH] Login error:', error)
          const code = error.response?.data?.code
          if (code === '2FA_CODE_REQUIRED' || code === '2FA_SETUP_REQUIRED') {
            return { success: false, code, error: error.response?.data?.bericht }
          }
          return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Login failed',
          }
        }
      },
      
      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        })
        delete api.defaults.headers.common['Authorization']
      },

      setUserSetupCompleted: (completed) => {
        const state = get()
        if (state.user) {
          set({ user: { ...state.user, is_setup_completed: !!completed } })
        }
      },

      /** Accepteer privacy policy met beperkt token, daarna opnieuw inloggen. */
      acceptPrivacyPolicy: async (ppToken) => {
        try {
          await api.post('/auth/accepteer-privacy-policy', null, {
            headers: { Authorization: `Bearer ${ppToken}` },
          })
          return { success: true }
        } catch (error) {
          return {
            success: false,
            error: error.response?.data?.detail || 'Privacyverklaring kon niet worden geaccepteerd.',
          }
        }
      },

      /** Zet accepted_tos_at in user (na accepteren ToS), zodat ToS-modal niet opnieuw verschijnt. */
      setUserTosAccepted: () => {
        const state = get()
        if (state.user) {
          set({ user: { ...state.user, accepted_tos_at: new Date().toISOString() } })
        }
      },
      
      setToken: (token) => {
        set({ token })
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`
      },

      /** Sync user from /auth/me – zorgt dat is_admin/role up-to-date zijn na elke login/herlaad */
      syncUserFromMe: async () => {
        const state = get()
        if (!state.token || !state.user) return
        try {
          const res = await api.get('/auth/me', { timeout: 8000 })
          const d = res?.data
          if (d && state.user) {
            const roleFromRol = d.rol?.naam ? String(d.rol.naam).toUpperCase() : null
            const isAdmin = d.is_admin || roleFromRol === 'ADMIN'
            set({
              user: {
                ...state.user,
                is_admin: isAdmin,
                is_superuser: d.is_superuser ?? state.user.is_superuser,
                role: roleFromRol || d.rol?.naam || state.user.role,
                // De backend is de bron: een bewaarde aanvaarding sluit het voorwaardenvenster.
                accepted_tos_at: d.accepted_tos_at ?? state.user.accepted_tos_at ?? null,
              },
            })
          }
        } catch {
          // Stil falen – token kan verlopen zijn
        }
      },
    }),
    {
      name: 'orion-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ token: state.token, user: state.user, isAuthenticated: state.isAuthenticated }),
      onRehydrateStorage: () => (state) => {
        // Restore token to API client after rehydration
        if (state?.token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${state.token}`
        }
      },
    }
  )
)

// Initialize on module load
useAuthStore.getState().initialize?.()
